'use client';

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import {
  fetchCourseContact,
  recordCourseInteraction,
} from '../../api/courses/course-statistics-api';

export function CourseContactActions({ courseId }: { courseId: number }) {
  const locale = useLocaleStore((s) => s.currentLocale);
  const { t } = useTranslations();
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const recorded = useRef(new Set<string>());
  const contact = useQuery({
    queryKey: ['course-public-contact', locale, courseId],
    queryFn: () => fetchCourseContact(courseId, locale).then((r) => r.data),
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (!contact.data || contact.isError || !Number.isSafeInteger(courseId) || courseId <= 0)
      return;
    const record = () => {
      const mountKey = `view:${courseId}`;
      if (document.visibilityState !== 'visible' || recorded.current.has(mountKey)) return;
      recorded.current.add(mountKey);
      const storageKey = `jobinterview:course-view:${courseId}`;
      const now = Date.now();
      try {
        const previous = Number(localStorage.getItem(storageKey));
        if (previous > 0 && now >= previous && now - previous < 60_000) return;
        localStorage.setItem(storageKey, String(now));
      } catch {
        /* The server also limits repeat views. */
      }
      void recordCourseInteraction(courseId, locale, 'view_course', crypto.randomUUID()).catch(
        () => {
          try {
            if (localStorage.getItem(storageKey) === String(now))
              localStorage.removeItem(storageKey);
          } catch {
            /* Storage may be disabled. */
          }
        },
      );
    };
    record();
    document.addEventListener('visibilitychange', record);
    return () => document.removeEventListener('visibilitychange', record);
  }, [courseId, locale, contact.data, contact.isError]);

  if (contact.isPending) return <p role="status">{t('Načítavam kontakty…')}</p>;
  if (contact.isError)
    return (
      <div>
        <p role="status">{t('Kontakty sa nepodarilo načítať.')}</p>
        <Button type="button" variant="outline" onClick={() => void contact.refetch()}>
          {t('Skúsiť znova')}
        </Button>
      </div>
    );

  const reveal = (channel: 'phone' | 'email') => {
    const key = `${courseId}:${channel}`;
    setRevealed((previous) => ({ ...previous, [key]: true }));
    if (recorded.current.has(key)) return;
    recorded.current.add(key);
    void recordCourseInteraction(
      courseId,
      locale,
      channel === 'phone' ? 'click_show_phone' : 'click_show_email',
      crypto.randomUUID(),
    ).catch(() => {});
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      {(['phone', 'email'] as const).map((channel) => {
        const value = contact.data?.[channel];
        if (!value) return null;
        return revealed[`${courseId}:${channel}`] ? (
          <a
            key={channel}
            className="break-all text-primary underline"
            href={`${channel === 'phone' ? 'tel' : 'mailto'}:${value}`}
          >
            {value}
          </a>
        ) : (
          <Button key={channel} type="button" variant="outline" onClick={() => reveal(channel)}>
            {channel === 'phone' ? t('Zobraziť telefón') : t('Zobraziť e-mail')}
          </Button>
        );
      })}
    </div>
  );
}
